import { apiBase } from 'api/api';
import { ApiResponse } from 'api/interface/api';
import { formatWithPattern } from 'util/dateFnsHelpers';
import { handleBlobReportResponse } from 'util/downloadReport';
import { cleanAddressField } from 'util/helper';
import {
    GetListOfParkingLotsRequest,
    GetConnectedUsersForParkingLotRequest,
    GetConnectedUsersForParkingLotResponse,
    GetParkingLotRequest,
    GetParkingLotResponseBackoffice,
    UpdateParkingLotRequest,
    UpdateParkingLotResponse,
    DeleteSecondaryImageRequest,
    DeleteSecondaryImageResponse,
    DeleteParkingAddressRequest,
    DeleteParkingAddressResponse,
    UploadSecondaryImageRequest,
    UploadSecondaryImageResponse,
    UploadProfileImageRequest,
    UploadProfileImageResponse,
    CreateParkingRequest,
    CreateParkingResponse,
    ParkingSpotResponse,
    CreateTicketRuleResponse,
    CreateTicketRuleRequest,
    UpdateTicketRuleRequest,
    UpdateTicketRuleResponse,
    GetParkinglotTicketRulesRequest,
    GetListOfParkingsResponse,
    ParkingTicketRulesResponseV2,
    GetTicketRuleByIdRequest,
    GetTicketRuleResponse,
    TicketRulesv2,
    UploadTicketRuleImageRequest,
    UploadTicketRuleImageResponse,
    GetListOfOrganisationsResponse,
    UpdateParkingSpotsRequest,
    UpdateParkingSpotsResponse,
    GetListOfAreasRequest,
    GetListOfAreasResponse,
    GetAreaInfoByIdRequest,
    GetAreaInfoByIdResponse,
    ParkingLotSpotsSectionsSubsResponse,
    UpdateParkingSectionsRequest,
    UpdateParkingSectionsResponse,
    getNearbyAddressesRequest,
    getNearbyAddressesResponse,
    NearbyAddress,
    ParkingLot,
    GetParkingLotWithCamerasRequest,
    GetParkingLotWithCamerasResponse,
    ParkingLotsWithCameras,
    ParkingZoneCoordinates,
    AddressCoordinate,
    ParkingZoneIDCoordinates,
    DmrDontSendFineAddressDTO,
    GetDmrDontSendFineAddressResponse,
    UpdateDmrDontSendFineAddressResponse,
} from 'api/interface/parking';

const getListOfParkingLots = async (
    req: GetListOfParkingLotsRequest
): Promise<GetListOfParkingsResponse> => {
    const queryParams = new URLSearchParams();

    queryParams.append('limit', req.limit.toString());
    queryParams.append('offset', req.offset.toString());
    queryParams.append('organisation_ids', req.organisation_ids ?? '');
    queryParams.append('show_active', String(req.active));
    queryParams.append('search_term', req.search_term ?? '');
    queryParams.append('sort_order', req.sort_order ?? '');
    queryParams.append('sort_col', req.sort_col ?? '');
    queryParams.append('minimal', String(req.minimal));
    queryParams.append(
        'show_spots_and_groups',
        String(req.showSpotsAndGroups ?? true)
    );
    queryParams.append('show_parking_zones', String(req.showZones ?? true));
    queryParams.append('parkinglot_ids', req.parkinglot_ids ?? '');

    const res = await apiBase.get(`/v3/parkinglots?${queryParams.toString()}`);

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {
                total: 0,
                data: [],
            },
        };
    }
    res.data.parking_lots = (res.data.parking_lots ?? []).map(
        (item: ParkingLot) => ({
            ...item,
            street_name: cleanAddressField(item.street_name),
            postal_code: cleanAddressField(item.postal_code),
            street_number: cleanAddressField(item.street_number),
            city: cleanAddressField(item.city),
        })
    );
    return {
        error: null,
        statusCode: res.status,
        payload: {
            data: res.data.parking_lots,
            total: res.data.total,
        },
    } as GetListOfParkingsResponse;
};

const getCoordinatesForParkingLot = async (
    req: GetListOfParkingLotsRequest
): Promise<ParkingZoneCoordinates> => {
    const res = await apiBase.get(
        `/v3/parkinglots/coordinates?
        &organisation_ids=${req.organisation_ids}&show_active=${req.active}
        &show_spots_and_groups=${
            req.showSpotsAndGroups || true
        }&show_parking_zones=${req.showZones || true}&show_patrol_areas=${req.showZones || true}`
    );

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: [],
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as ParkingZoneCoordinates;
};

const getCoordinatesForParkingLotById = async (
    parkingLotId: number
): Promise<ParkingZoneIDCoordinates> => {
    const res = await apiBase.get(
        `/v3/parkinglots/${parkingLotId}/coordinates`
    );

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {} as AddressCoordinate,
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as ParkingZoneIDCoordinates;
};

const filterGetListOfParkingLots =
    async (): Promise<GetListOfParkingsResponse> => {
        const res = await apiBase.get(`/v3/platform/address/dropdown`);

        if (res.status !== 200) {
            return {
                error: 'bad request',
                statusCode: res.status,
                payload: {
                    data: [],
                    total: 0,
                },
            };
        }
        res.data = res.data.map((item: ParkingLot) => ({
            ...item,
            street_name: cleanAddressField(item.street_name),
            postal_code: cleanAddressField(item.postal_code),
            street_number: cleanAddressField(item.street_number),
            city: cleanAddressField(item.city),
        }));
        return {
            error: null,
            statusCode: res.status,
            payload: {
                data: res.data,
                total: res.data.length,
            },
        };
    };

const getParkingLot = async (
    req: GetParkingLotRequest
): Promise<GetParkingLotResponseBackoffice> => {
    const res = await apiBase.get(`/v3/parkinglots/${req.id}`);
    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        };
    }

    if (res.data.permit_length === null) {
        res.data.permit_length = 0;
    }
    if (res.data.no_permits === null) {
        res.data.no_permits = 0;
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as GetParkingLotResponseBackoffice;
};

const updateParkingLot = async ({
    updatedParkingLot,
}: UpdateParkingLotRequest): Promise<UpdateParkingLotResponse> => {
    const res = await apiBase.put(`/v3/parkinglots/${updatedParkingLot.id}`, {
        ...updatedParkingLot,
    });
    if (res.status !== 200) {
        return {
            error: res.data.message,
            statusCode: res.status,
        };
    }

    return {
        statusCode: res.status,
        error: null,
        payload: res.data.payload,
    } as UpdateParkingLotResponse;
};

const deleteSecondaryImage = async (
    req: DeleteSecondaryImageRequest
): Promise<DeleteSecondaryImageResponse> => {
    const res = await apiBase.delete('/v3/parkinglots/image', {
        data: {
            id: req.address_id,
            image: req.fileName,
        },
    });

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data.payload,
    } as DeleteSecondaryImageResponse;
};

export const deleteParkingAddress = async (
    req: DeleteParkingAddressRequest
): Promise<DeleteParkingAddressResponse> => {
    const res = await apiBase.delete(`/v3/parkinglots/${req.address_id}`);

    if (res.status !== 200) {
        return { error: 'bad request', statusCode: res.status };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: { message: res.data.message },
    };
};

const uploadSecondaryImage = async (
    req: UploadSecondaryImageRequest,
    userId: number
): Promise<UploadSecondaryImageResponse> => {
    const data = new FormData();
    data.append('user_id', String(userId));
    data.append('status', String(0));
    data.append('deleted', String(0));
    data.append('address_id', req.address_id.toString());

    if (req.image_type_id != null) {
        data.append('image_type_id', String(req.image_type_id));
    }

    for (let i = 0; i < req.arrayFile.length; ++i) {
        data.append('file', req.arrayFile[i]);
        data.append(
            'timestamp',
            formatWithPattern(new Date(), 'YYYY-MM-DD hh mm ss')
        );
    }

    const res = await apiBase.post('/v3/parkinglots/image', data);
    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            images: [],
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data.payload,
        images: res.data.images,
    } as UploadSecondaryImageResponse;
};

const uploadProfileImage = async (
    req: UploadProfileImageRequest
): Promise<UploadProfileImageResponse> => {
    const data = new FormData();
    data.append('id', req.address_id.toString());
    for (let i = 0; i < req.files.length; ++i) {
        data.append('file', req.files[i]);
        data.append(
            'timestamp',
            formatWithPattern(new Date(), 'YYYY-MM-DD hh mm ss')
        );
    }
    const res = await apiBase.put('/v3/parkinglots/top-image', data);
    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
        image: res.data.image,
    } as UploadProfileImageResponse;
};

const createParking = async (
    req: CreateParkingRequest
): Promise<CreateParkingResponse> => {
    delete req.file;
    delete req.start_date;
    delete req.created_at;
    const res = await apiBase.post('/v3/parkinglots', req);

    if (res.status !== 200) {
        return {
            error: res.data?.message || res.data?.error || 'bad request',
            statusCode: res.status,
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: {
            message: res.data.success,
            address_id: res.data.address_id,
        },
    } as CreateParkingResponse;
};

const PARKING_LOT_REPORT_ROW_LIMIT =
    'Unable to generate report. The amount of rows exceeded 200,000 — please apply filters to minimize the amount.';

const generateParkingLotReport = async (
    req: GetListOfParkingLotsRequest
): Promise<ApiResponse> => {
    const res = await apiBase.get(
        `/v3/parkinglots/report?organisation_ids=${
            req.organisation_ids
        }&show_active=${req.active}&search_term=${req.search_term}&sort_order=${
            req.sort_order
        }&sort_col=${req.sort_col}&show_spots_and_groups=${
            req.showSpotsAndGroups || false
        }&show_parking_zones=${req.showZones || true}`,
        {
            responseType: 'blob',
            timeout: 0,
        }
    );

    const result = await handleBlobReportResponse(res, {
        non200Error: PARKING_LOT_REPORT_ROW_LIMIT,
    });

    return {
        error: result.error,
        statusCode: result.statusCode,
    } as ApiResponse;
};

const getParkingSpots = async (
    parkingLotId: number
): Promise<ParkingSpotResponse> => {
    const res = await apiBase.get(
        `/backoffice/parkinglot/${parkingLotId}/spots`
    );
    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: {
            data: res.data.available_parking_spots,
            total: res.data.available_parking_spots.length,
        },
    } as ParkingSpotResponse;
};

const getConnectedUsersForParkingLot = async (
    req: GetConnectedUsersForParkingLotRequest
): Promise<GetConnectedUsersForParkingLotResponse> => {
    const res = await apiBase.get(`/v3/parkinglots/${req.id}/connected-users`);

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: [],
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    };
};

const getParkinglotTicketRules = async (
    req: GetParkinglotTicketRulesRequest
): Promise<ParkingTicketRulesResponseV2> => {
    const res = await apiBase.get(
        `/v3/ticketrules?offset=${req.offset}&limit=${req.limit}&sort_col=${req.sort_col}&sort_order=${req.sort_order}&search_term=${req.search_term}&parkinglot_ids=${req.parkinglot_ids}&organisation_ids=${req.organisation_ids}&type=${req.type}`
    );

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            data: [],
            total: 0,
        } as ParkingTicketRulesResponseV2;
    }

    const mappedData: ParkingTicketRulesResponseV2[] = res.data.data.map(
        (item: TicketRulesv2) => ({
            ticket: {
                id: item.ticket.id,
                url_id: item.ticket.url_id,
                type: item.ticket.type,
            },
            address: {
                address_name: item.address.address_name,
                city: item.address.city,
            },
        })
    );

    return {
        error: null,
        statusCode: res.status,
        data: mappedData,
        total: res.data.total,
    } as unknown as ParkingTicketRulesResponseV2;
};

const getTicketRulesById = async (
    req: GetTicketRuleByIdRequest
): Promise<GetTicketRuleResponse> => {
    const res = await apiBase.get(`/v3/ticketrules/${req.ID}`);

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            data: [],
        };
    }
    return {
        error: null,
        statusCode: res.status,
        data: res.data.data,
    } as GetTicketRuleResponse;
};

const createParkinglotTicketRule = async (
    req: CreateTicketRuleRequest
): Promise<CreateTicketRuleResponse> => {
    const ticket_rules = req.ticket_rules.map((ticketRuleOption) => ({
        ...ticketRuleOption,
    }));

    const res = await apiBase.post(`/v3/ticketrules`, {
        url_id: req.name,
        parking_address_id: req.address_id,
        ticket_rules,
        service_fee: req.service_fee,
        type: req.type,
        discount_enabled: req.discount_enabled,
        external_note: req.external_note,
        autoflow_limit: req.autoflow_limit,
    });

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: [],
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data.ticket,
    } as CreateTicketRuleResponse;
};

const updateParkinglotTicketRule = async (
    req: UpdateTicketRuleRequest
): Promise<UpdateTicketRuleResponse> => {
    const updatedTicketsRules = req.ticket_rules.map((ticketRuleOption) => {
        return {
            ...ticketRuleOption,
        };
    });
    const res = await apiBase.put(`/v3/ticketrules`, {
        parking_address_id: req.address_id,
        ticket_rules: updatedTicketsRules,
        old_url_id: req.url_id,
        url_id: req.name,
        service_fee: req.service_fee,
        discount_enabled: req.discount_enabled,
        external_note: req.external_note,
        ticketRule_image: req.ticketRule_image,
        is_running_time: req.is_running_time,
        autoflow_limit: req.autoflow_limit,
    });

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: [],
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data.payload,
    } as UpdateTicketRuleResponse;
};

const uploadTicketRuleImage = async (
    req: UploadTicketRuleImageRequest
): Promise<UploadTicketRuleImageResponse> => {
    const data = new FormData();
    data.append('id', req.address_id.toString());
    for (let i = 0; i < req.files.length; ++i) {
        data.append('file', req.files[i]);
        data.append(
            'timestamp',
            formatWithPattern(new Date(), 'YYYY-MM-DD hh mm ss')
        );
    }
    const res = await apiBase.put('/v3/ticketrules/image', data);
    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
        image: res.data.image,
    } as UploadTicketRuleImageResponse;
};

const getListOfOrganisations = async (
    parkingLotID: number
): Promise<GetListOfOrganisationsResponse> => {
    const res = await apiBase.get(
        `/v3/parkinglots/organisations/${parkingLotID}`
    );

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {
                data: [],
                total: 0,
            },
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: {
            data: res.data.organisations,
            total: res.data.total,
        },
    } as GetListOfOrganisationsResponse;
};

const getListOfParkingAreas = async (
    req: GetListOfAreasRequest
): Promise<GetListOfAreasResponse> => {
    const res = await apiBase.get(
        `v3/parkinglots/areas?limit=${req.limit}&offset=${req.offset}&organisation_ids=${req.organisation_ids}&search_term=${req.search_term}&sort_order=${req.sort_order}&sort_col=${req.sort_col}&minimal=${req.minimal}`
    );

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {
                total: 0,
                data: [],
            },
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: {
            data: res.data.parking_lots,
            total: res.data.total,
        },
    } as GetListOfParkingsResponse;
};

export const updateParkingSpots = async (
    req: UpdateParkingSpotsRequest
): Promise<UpdateParkingSpotsResponse> => {
    const formattedSpots = req.parking_spots.map((spot) => ({
        ...spot,
    }));

    const res = await apiBase.put(`v3/parkinglots/spots`, {
        address_id: req.parking_lot_id,
        parking_spots: formattedSpots,
    });

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: [],
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data.payload,
    };
};

const getAreaInfoById = async (
    req: GetAreaInfoByIdRequest
): Promise<GetAreaInfoByIdResponse> => {
    const res = await apiBase.get(`v3/parkinglots/areas/${req.parkingLotID}`);

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: undefined,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    };
};

export const getParkingLotSpotsSectionsSubs = async (
    parkingLotID: number
): Promise<ParkingLotSpotsSectionsSubsResponse> => {
    const res = await apiBase.get(
        `v3/parkinglots/areas/${parkingLotID}/spots-sections`
    );

    if (res.status !== 200) {
        return {
            error: 'Failed to fetch spot and section data',
            statusCode: res.status,
            payload: undefined,
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    };
};

export const updateParkingSections = async (
    req: UpdateParkingSectionsRequest
): Promise<UpdateParkingSectionsResponse> => {
    const res = await apiBase.put('v3/parkinglots/sections', {
        address_id: req.address_id,
        sections: req.sections,
    });

    if (res.status !== 200) {
        return {
            error: 'Failed to update parking sections',
            statusCode: res.status,
            payload: undefined,
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    };
};

export const getNearbyAddresses = async (
    request: getNearbyAddressesRequest
): Promise<getNearbyAddressesResponse> => {
    const res = await apiBase.get(
        `/v3/platform/address/dropdown?latitude=${request.latitude}&longitude=${request.longitude}`
    );
    if (res.status !== 200) {
        return {
            payload: {},
            error: res.data.error,
            statusCode: res.status,
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: {
            data: res.data.map((d: NearbyAddress) => ({
                id: d.id,
                address_name: d.address_name,
                zipcode: d.zipcode,
                city: d.city,
            })),
        },
    } as getNearbyAddressesResponse;
};

export const GetParkingAddressesWithCameras = async (
    req: GetParkingLotWithCamerasRequest
): Promise<GetParkingLotWithCamerasResponse> => {
    try {
        const res = await apiBase.get<ParkingLotsWithCameras>(
            'v3/parkinglots/camera-counts',
            {
                params: {
                    limit: req.limit,
                    offset: req.offset,
                    search_term: req.search_term,
                    sort_col: req.sort_col ?? 'id',
                    sort_order: req.sort_order ?? 'desc',
                    parkinglot_ids: req.parkinglot_ids,
                    ...(req.status && { status: req.status }),
                },
            }
        );

        return {
            error: null,
            statusCode: res.status,
            payload: res.data,
        };
        // eslint-disable-next-line
    } catch (err: any) {
        const status = err?.response?.status ?? 500;
        const msg = err?.response?.data?.error ?? 'bad request';
        return {
            error: msg,
            statusCode: status,
            payload: { total: 0, parking_lots: [] },
        };
    }
};

export const getDmrDontSendFineAddresses = async (
    parkingLotId: number
): Promise<GetDmrDontSendFineAddressResponse> => {
    const res = await apiBase.get(
        `/v3/parkinglots/${parkingLotId}/no-fine-address`
    );

    if (res.status !== 200) {
        return { error: 'bad request', statusCode: res.status };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: {
            enabled: res.data.length > 0,
            addresses: res.data.map(
                (item: { no_fine_address: string }) => item.no_fine_address
            ),
        },
    } as GetDmrDontSendFineAddressResponse;
};

export const updateDmrDontSendFineAddresses = async (
    parkingLotId: number,
    data: DmrDontSendFineAddressDTO
): Promise<UpdateDmrDontSendFineAddressResponse> => {
    const res = await apiBase.put(
        `/v3/parkinglots/${parkingLotId}/no-fine-address`,
        data
    );

    if (res.status !== 200) {
        return { error: 'bad request', statusCode: res.status };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as UpdateDmrDontSendFineAddressResponse;
};

export const backofficeParkingApi = {
    getListOfParkingLots,
    getParkingLot,
    updateParkingLot,
    createParking,
    uploadSecondaryImage,
    uploadProfileImage,
    generateParkingLotReport,
    deleteSecondaryImage,
    deleteParkingAddress,
    getParkingSpots,
    getConnectedUsersForParkingLot,
    getTicketRulesById,
    getParkinglotTicketRules,
    createParkinglotTicketRule,
    updateParkinglotTicketRule,
    uploadTicketRuleImage,
    getListOfOrganisations,
    filterGetListOfParkingLots,
    getListOfParkingAreas,
    updateParkingSpots,
    getAreaInfoById,
    getParkingLotSpotsSectionsSubs,
    updateParkingSections,
    getNearbyAddresses,
    GetParkingAddressesWithCameras,
    getCoordinatesForParkingLot,
    getCoordinatesForParkingLotById,
    getDmrDontSendFineAddresses,
    updateDmrDontSendFineAddresses,
};
